import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import OnboardingController, { useOnboardingContext } from './OnboardingController';
import OnboardingHighlight from './OnboardingHighlight';
import OnboardingStep from './OnboardingStep';

const OnboardingTourContent = () => {
  const { isActive, currentStepData, skipOnboarding } = useOnboardingContext();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        skipOnboarding();
      }
    };

    if (isActive) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isActive, skipOnboarding]);

  if (!isActive) return null;

  return (
    <AnimatePresence mode="wait">
      {isActive && currentStepData && (
        <OnboardingHighlight
          targetSelector={currentStepData.target}
          isActive={isActive}
          padding={12}
          borderRadius={8}
        >
          <OnboardingStep step={currentStepData} />
        </OnboardingHighlight>
      )}
    </AnimatePresence>
  );
};

const OnboardingTourWrapper = ({ autoStart }) => {
  const { startOnboarding, hasCompleted, resetOnboarding } = useOnboardingContext();

  useEffect(() => {
    // Listen for custom event to start onboarding
    const handleStartOnboarding = () => {
      resetOnboarding();
      setTimeout(() => startOnboarding(), 100);
    };

    window.addEventListener('startOnboarding', handleStartOnboarding);

    return () => {
      window.removeEventListener('startOnboarding', handleStartOnboarding);
    };
  }, [resetOnboarding, startOnboarding]);

  useEffect(() => {
    // Check if we've already prompted onboarding in this session
    const hasPromptedThisSession = sessionStorage.getItem('pickaiOnboardingPrompted');

    if (autoStart && !hasCompleted && !hasPromptedThisSession) {
      // Mark that we've prompted onboarding in this session
      sessionStorage.setItem('pickaiOnboardingPrompted', 'true');

      const timer = setTimeout(() => {
        startOnboarding();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [autoStart, hasCompleted, startOnboarding]);

  return <OnboardingTourContent />;
};

const OnboardingTour = ({ onComplete, onSkip, autoStart = false, userId }) => {
  return (
    <OnboardingController onComplete={onComplete} onSkip={onSkip} userId={userId}>
      <OnboardingTourWrapper autoStart={autoStart} />
    </OnboardingController>
  );
};

export default OnboardingTour;